import {Spinner, SimpleGrid, Text, VStack,Center } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";

import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import { useStores } from "../../store/store_context";
import DateFilter from "./date_filter";
import baseUrl from "../../vars";
import { authFetch } from "../../auth/auth";

const GraphicBlock = observer(() => {
  const { pageStore } = useStores();
  const params1 = new URLSearchParams();

  if (pageStore.selected_managers && pageStore.selected_managers != "") {
    pageStore.selected_managers?.forEach((manager) => {
      params1.append("manager", manager);
    });
  }

  const getAllPerformance = async () => {
    pageStore.setLoading(true)
    const response = await authFetch(
      `${baseUrl}/api/performance?start=${pageStore.start_date}&end=${
        pageStore.end_date
      }${pageStore.selected_managers == "" ? "" : `&${params1.toString()}`}${
        pageStore.selected_one_department == ""
          ? ""
          : `&section=${pageStore.selected_one_department}`
      }`,{
        method: "GET",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${pageStore.token}`,
        },
      }
    );
    const result = await response.json();
    const sortedData = result.data.sort(
      (a, b) => a.dateCreated - b.dateCreated
    );
    pageStore.updatePerformanceData(sortedData);
    
    pageStore.setLoading(false)
  };

  useEffect(() => {
  const handler = setTimeout(() => {
    getAllPerformance();
  }, 2000); 

  return () => {
    clearTimeout(handler);
  };
}, [
    pageStore.selected_managers,
    pageStore.start_date,
    pageStore.end_date,
    pageStore.selected_one_department,
  ]);

  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
  );

  const metricPairs = [
    "clients_processed",
    "calls_made",
    "lids_got",
    "sent_to_purchase_dep",
    "commercial_offer",
    "orders",
    "sum_commercial_offer",
    "sum_orders",
    "pays",
  ];

  const metricLabels = {
    clients_processed: "Проработано клиентов",
    calls_made: "Количество звонков более 40 сек",
    lids_got: "Получено заявок",
    sent_to_purchase_dep: "Отправлено в ОЗ",
    commercial_offer: "Выставлено КП",
    orders: "Выставлено Счетов",
    sum_commercial_offer: "Общая сумма КП",
    sum_orders: "Общая сумма Счетов",
    pays: "Получено оплат",
  };

  const hashColor = (key) => {
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = key.charCodeAt(i) + ((hash << 5) - hash);
    }
    const r = (hash >> 24) & 255;
    const g = (hash >> 16) & 255;
    const b = (hash >> 8) & 255;
    return `rgba(${r}, ${g}, ${b}, 1)`;
  };

  const generateChartData = (metrics, isGrouped) => {
    const years = pageStore.performance_data.map((item) =>
      new Date(item.dateCreated).getFullYear()
    );
    const isSingleYear = new Set(years).size === 1;

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return isSingleYear
        ? date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" })
        : date.toLocaleDateString("en-GB");
    };

    const groupedData = pageStore.performance_data.reduce((acc, item) => {
      const dateLabel = formatDate(item.dateCreated);
      if (!acc[dateLabel]) acc[dateLabel] = {};
      metrics.forEach((metric) => {
        if (!acc[dateLabel][metric]) acc[dateLabel][metric] = 0;
        acc[dateLabel][metric] += item[metric] || 0;
      });
      return acc;
    }, {});

    const labels = Object.keys(groupedData).sort((a, b) => {
      const dateA = new Date(a.split("/").reverse().join("-"));
      const dateB = new Date(b.split("/").reverse().join("-"));
      return dateA - dateB; 
    });

    const datasets = isGrouped
      ? metrics.map((metric) => {
          const data = [];
          let lastValidIndex = -1;

          labels.forEach((date, index) => {
            if (groupedData[date] && groupedData[date][metric] !== undefined) {
              data.push(groupedData[date][metric]);
              lastValidIndex = index; 
            } else {
              data.push(index <= lastValidIndex ? 0 : null);
            }
          });

          return {
            label: metricLabels[metric],
            data,
            borderColor: hashColor(metric),
            backgroundColor: hashColor(metric),
            pointStyle: "circle",
            pointRadius: 4,
            tension: 0.2,
            spanGaps: true,
          };
        })
      : pageStore.all_managers.map((manager) => {
          const data = [];

          labels.forEach((date) => {
            const record = pageStore.performance_data.find(
              (item) =>
                item.fio === manager.fio &&
                formatDate(item.dateCreated) === date
            );

            if (record && metrics[0] in record) {
              data.push(record[metrics[0]]);
            } else {
              data.push(0); 
            }
          });

          return {
            label: manager?.fio,
            data,
            borderColor: hashColor(manager.fio),
            backgroundColor: hashColor(manager.fio),
            pointStyle: "circle",
            pointRadius: 4,
            tension: 0.2,
            spanGaps: true, 
          };
        }).filter((dataset) => dataset.data.some((val) => val !== null && val !== 0));

    return { labels, datasets };
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: { display: true, color: "#444" },
        ticks: { color: "#EEEEEE" },
      },
      y: {
        grid: { display: true, color: "#444" },
        ticks: { color: "#EEEEEE" },
      },
    },
    plugins: {
      legend: {
        position: "top",
        labels: {
          color: "#eeeeee",
          usePointStyle: true,
          font: { size: 12 },
        },
      },
    },
  };

  const renderGraphsForMultipleManagers = () =>
  metricPairs.map((item, index) => (
    <VStack
      height={"450px"}
      width={"100%"}
      zIndex={2}
      key={index}
      bg="#1e1e1e"
      padding={"20px 0 40px 0"}
      borderRadius={"10px"}
    >
      {Object.entries(metricLabels)
        ?.find((item2, index2) => index == index2)
        .filter((item4, index4) => index4 % 2 == 1)
        .map((item3, index) => (
          <Text color={"white"} fontWeight={"600"} fontSize={"14px"} key={index}>
            {item3}
          </Text>
        ))}

      <Line data={generateChartData([item], false)} options={options} />
    </VStack>
  ));
  if (pageStore.loading) {
    return (
      <Center w="100%" h="400px">
        <Spinner size="xl" color="white" thickness="4px" />
      </Center>
    );
  }
  return (
  <SimpleGrid
    padding="10px"
    borderRadius="16px"
    height="100%"
    gap="20px"
    w="100%"
    columns={{ base: 1, md: 2 }}
  >
    {renderGraphsForMultipleManagers()}
  </SimpleGrid>
);
});

export default GraphicBlock;
